import {
  MutableRefObject,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import {
  GridApiPro,
  DataGridProProps,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { logError } from 'setup/logger/logError';
import { DataGridInstanceName } from 'services/storage/keys';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import type { VersionedGridState } from '../columnsFactory/types';

function exportState(
  apiRef: MutableRefObject<GridApiPro | null>,
  dataGridInstanceName: DataGridInstanceName
): GridInitialState | null {
  if (!apiRef.current) {
    return null;
  }

  const exported = apiRef.current.exportState() as GridInitialState;

  const savedState = LocalStorageManager.getDataGridState(
    dataGridInstanceName
  ) as VersionedGridState | null;

  if (savedState?._version !== undefined) {
    return { ...exported, _version: savedState._version } as GridInitialState;
  }

  return exported;
}

function removeRowCountFromState(state: GridInitialState): GridInitialState {
  if (state.pagination?.rowCount === undefined) {
    return state;
  }

  const { rowCount: _, ...paginationWithoutRowCount } = state.pagination;
  return {
    ...state,
    pagination: paginationWithoutRowCount,
  };
}

export function useDataGridStateStorage(
  apiRef: MutableRefObject<GridApiPro | null>,
  dataGridInstanceName: DataGridInstanceName,
  autosizeOptions?: DataGridProProps['autosizeOptions'],
  autosizeOnMount?: boolean
): void {
  const [isAutosizingComplete, setIsAutosizingComplete] = useState(false);
  const autosizeOptionsRef = useRef(autosizeOptions);
  autosizeOptionsRef.current = autosizeOptions;

  // Initial setup: restore state and run autosizing if needed.
  // Do NOT depend on autosizeOptions (new object every render) to avoid infinite loop (React #185).
  useEffect(() => {
    if (!apiRef?.current) return;

    const savedState = LocalStorageManager.getDataGridState(
      dataGridInstanceName
    );
    const hasSavedColumnDimensions = Boolean(
      savedState?.columns?.dimensions &&
        Object.keys(savedState.columns.dimensions).length > 0
    );
    const hasSavedColumnOrder = Boolean(
      savedState?.columns?.orderedFields &&
        savedState.columns.orderedFields.length > 0
    );
    const hasSavedColumnsState =
      hasSavedColumnDimensions || hasSavedColumnOrder;

    if (savedState) {
      try {
        apiRef.current.restoreState(removeRowCountFromState(savedState));
      } catch (error) {
        logError('Failed to restore DataGrid state', error);
      }
    }

    const opts = autosizeOptionsRef.current;
    const shouldAutosize =
      !hasSavedColumnsState &&
      opts &&
      autosizeOnMount !== false &&
      apiRef.current;

    if (shouldAutosize) {
      try {
        apiRef.current.autosizeColumns(opts);

        requestAnimationFrame(() => {
          if (apiRef.current) {
            try {
              const exportedState = exportState(apiRef, dataGridInstanceName);

              if (!exportedState) {
                setIsAutosizingComplete(true);
                return;
              }

              LocalStorageManager.setDataGridState(
                dataGridInstanceName,
                removeRowCountFromState(exportedState)
              );
              setIsAutosizingComplete(true);
            } catch (error) {
              logError(
                'Failed to save DataGrid state after autosizing',
                error
              );
            }
          }
        });
      } catch (error) {
        logError('Failed to autosize columns on first render', error);
        setIsAutosizingComplete(true);
      }
    } else {
      setIsAutosizingComplete(true);
    }
  }, [
    apiRef,
    dataGridInstanceName,
    autosizeOnMount,
  ]);

  const saveState = useCallback(() => {
    if (!apiRef?.current) return;

    try {
      const exportedState = exportState(apiRef, dataGridInstanceName);

      if (!exportedState) {
        return;
      }

      LocalStorageManager.setDataGridState(
        dataGridInstanceName,
        removeRowCountFromState(exportedState)
      );
    } catch (error) {
      logError('Failed to save DataGrid state', error);
    }
  }, [apiRef, dataGridInstanceName]);

  // Subscribe to state changes only after autosizing is complete
  useEffect(() => {
    if (!apiRef?.current || !isAutosizingComplete) return;

    const unsubscribeStateChange = apiRef.current.subscribeEvent(
      'stateChange',
      saveState
    );

    return () => {
      saveState();
      unsubscribeStateChange();
    };
  }, [apiRef, saveState, isAutosizingComplete]);
}
